import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/api/client";
import type { SiteAuditLogPage } from "@/api/types";

export function useSiteAuditLog(
  siteId: string | undefined,
  opts: { before?: string; limit?: number } = {},
) {
  const qs = new URLSearchParams();
  if (opts.before) qs.set("before", opts.before);
  if (opts.limit) qs.set("limit", String(opts.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return useQuery<SiteAuditLogPage>({
    queryKey: ["sites", siteId, "audit", opts.before ?? null, opts.limit ?? 50],
    queryFn: () => apiRequest<SiteAuditLogPage>(`/api/sites/${siteId}/audit-events${suffix}`),
    enabled: Boolean(siteId),
  });
}

/** Cohort-scoped audit log. Powers the eTMF audit-trail viewer —
 * the trail is restricted to the master cohort participations at
 * (site, DA) plus their tasks, location artifacts, investigator
 * assignments, and document events. */
export function useCohortAuditLog(
  siteId: string | undefined,
  daId: string | undefined,
  opts: { enabled?: boolean; limit?: number } = {},
) {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set("limit", String(opts.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return useQuery<SiteAuditLogPage>({
    queryKey: ["cohorts", siteId, daId, "audit", opts.limit ?? 200],
    queryFn: () =>
      apiRequest<SiteAuditLogPage>(
        `/api/sites/${siteId}/cohorts/${daId}/audit-events${suffix}`,
      ),
    enabled: Boolean(siteId) && Boolean(daId) && (opts.enabled ?? true),
  });
}

/** Global audit log. Pass `siteId` to filter to one site. Used by the
 * admin-shaped Audit Log page under /clinops/audit. */
export function useAuditLog(
  opts: { siteId?: string | null; before?: string; limit?: number } = {},
) {
  const qs = new URLSearchParams();
  if (opts.siteId) qs.set("site_id", opts.siteId);
  if (opts.before) qs.set("before", opts.before);
  if (opts.limit) qs.set("limit", String(opts.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return useQuery<SiteAuditLogPage>({
    queryKey: [
      "audit",
      opts.siteId ?? null,
      opts.before ?? null,
      opts.limit ?? 50,
    ],
    queryFn: () => apiRequest<SiteAuditLogPage>(`/api/audit-events${suffix}`),
  });
}
